import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readPresentation, titlePlaceholder } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionNavigation: Lesson = {
  id: "navigation",
  title: "Navigation in langen Präsentationen",
  summary: "Foliennummern, Agendafolien und Zoom – damit man sich zurechtfindet.",
  durationMinutes: 9,
  learningGoals: [
    "Foliennummern über die Kopf- und Fußzeilenfunktion einblenden",
    "Eine Agendafolie sinnvoll aufbauen",
    "Zoom-Folien als interaktives Inhaltsverzeichnis nutzen",
  ],
  steps: [
    {
      id: "foliennummern",
      title: "Foliennummern",
      explanation: [
        "Foliennummern sind in langen Präsentationen ein Werkzeug für das Publikum: Sie " +
          "erlauben es, sich in der Fragerunde auf eine bestimmte Folie zu beziehen. Ohne " +
          "Nummer heißt es „auf der Folie mit dem blauen Diagramm“, was regelmäßig " +
          "danebengeht.",
        "Eingeblendet werden sie über „Einfügen“ → „Kopf- und Fußzeile“ → Häkchen bei " +
          "„Foliennummer“ → „Für alle übernehmen“. Auf der Titelfolie stört die Nummer " +
          "meist; dafür gibt es im selben Dialog das Häkchen „Auf Titelfolie nicht " +
          "anzeigen“.",
        "Die Nummer ist ein Feld, kein getippter Text. Sie zählt automatisch weiter, " +
          "wenn Folien eingefügt, gelöscht oder verschoben werden – dieselbe Logik wie " +
          "beim Seitenzahlenfeld im Word-Kurs und aus demselben Grund die einzig " +
          "brauchbare Lösung.",
        "Wo die Nummer steht und wie sie aussieht, entscheidet der Folienmaster. Wer sie " +
          "größer, an anderer Stelle oder in anderer Farbe haben will, ändert das dort " +
          "einmal statt auf jeder Folie.",
      ],
      keyPoints: [
        "Einfügen → Kopf- und Fußzeile → Foliennummer → Für alle übernehmen.",
        "„Auf Titelfolie nicht anzeigen“ lässt die Titelfolie frei.",
        "Position und Aussehen der Nummer kommen aus dem Folienmaster.",
      ],
      task: {
        instruction: "Blende in deiner Präsentation die Foliennummern ein.",
        hint: "Einfügen → Kopf- und Fußzeile → Häkchen bei „Foliennummer“ → „Für alle übernehmen“.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.placeholders) {
            return nichtPruefbar(
              "Die Platzhalterart einer Form",
              "1.8",
              "Sieh selbst nach, ob unten auf deinen Folien eine Nummer steht.",
            );
          }
          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst Folien an.",
            );
          }

          const mitNummer = snapshot.slides.filter((folie) =>
            folie.shapes.some((form) => form.placeholder?.type === "SlideNumber"),
          );

          if (mitNummer.length === 0) {
            return fail(
              "Auf keiner Folie ist ein Foliennummern-Platzhalter eingeblendet.",
              "Einfügen → Kopf- und Fußzeile → Häkchen bei „Foliennummer“ setzen und „Für alle übernehmen“ drücken.",
            );
          }

          // Die Titelfolie darf bewusst ohne Nummer bleiben.
          const ohne = snapshot.slides.filter(
            (folie) =>
              folie.index > 0 &&
              !folie.shapes.some((form) => form.placeholder?.type === "SlideNumber"),
          );
          if (ohne.length > 0) {
            return fail(
              `Auf ${ohne.length === 1 ? "einer Folie" : `${ohne.length} Folien`} fehlt die Nummer, ` +
                `zum Beispiel auf Folie ${ohne[0].index + 1}.`,
              "Im Dialog „Kopf- und Fußzeile“ auf „Für alle übernehmen“ drücken statt nur auf „Übernehmen“.",
            );
          }

          return pass(
            `Die Foliennummer ist auf ${mitNummer.length} von ${snapshot.slides.length} Folien eingeblendet` +
              (mitNummer.length < snapshot.slides.length ? " – die Titelfolie bleibt frei." : "."),
          );
        },
      },
    },
    {
      id: "agenda",
      title: "Die Agendafolie",
      explanation: [
        "Eine Agendafolie am Anfang beantwortet zwei Fragen: Worum geht es, und wie " +
          "lange dauert es? Beides senkt die Unruhe im Raum spürbar.",
        "Gebaut wird sie am besten aus den Abschnittsnamen – drei bis fünf Punkte, " +
          "keine Auflistung aller Folientitel. Eine Agenda mit zwanzig Punkten ist keine " +
          "Orientierung, sondern eine Ankündigung von Länge.",
        "In längeren Vorträgen lohnt es sich, die Agenda zwischendurch zu wiederholen " +
          "und den aktuellen Punkt hervorzuheben. Der einfachste Weg dafür: die " +
          "Agendafolie duplizieren, vor jeden Abschnitt setzen und jeweils den aktuellen " +
          "Punkt fett auszeichnen oder die übrigen ausgrauen.",
        "Wichtig ist, dass die Agendafolie zum Rest passt. Wer die Abschnitte später " +
          "umsortiert, muss die Agenda anpassen – sie ist eine Kopie, keine Verknüpfung. " +
          "Genau das lösen die Zoom-Folien im nächsten Schritt.",
      ],
      keyPoints: [
        "Drei bis fünf Punkte, aus den Abschnittsnamen.",
        "In langen Vorträgen die Agenda vor jedem Abschnitt wiederholen.",
        "Eine getippte Agenda ist eine Kopie und muss bei Umbauten nachgezogen werden.",
      ],
      task: {
        instruction:
          "Lege am Anfang deiner Präsentation eine Agendafolie an: ein Titel wie " +
          "„Agenda“, „Überblick“ oder „Inhalt“ und darunter drei bis fünf Stichpunkte.",
        hint: "Neue Folie mit dem Layout „Titel und Inhalt“, Titel „Agenda“, dann die Abschnittsnamen als Stichpunkte.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Der Text in den Platzhaltern",
              "1.4",
              "Prüfe selbst, ob am Anfang eine Agendafolie mit drei bis fünf Punkten steht.",
            );
          }

          const muster = /^(agenda|überblick|uberblick|inhalt|inhalte|gliederung|ablauf|themen)\b/i;
          const kandidat = snapshot.slides.find((folie) => {
            const titel = titlePlaceholder(folie)?.paragraphs[0] ?? "";
            return muster.test(titel.trim());
          });

          if (!kandidat) {
            return fail(
              "Es wurde keine Folie mit einem Agendatitel gefunden.",
              "Gesucht wird nach einem Titel, der mit „Agenda“, „Überblick“, „Inhalt“, „Gliederung“, „Ablauf“ oder „Themen“ beginnt.",
            );
          }

          const punkte = kandidat.shapes
            .filter(
              (form) =>
                form.type === "Placeholder" &&
                form.placeholder?.type !== "Title" &&
                form.placeholder?.type !== "CenterTitle" &&
                !/^(title|titel)\b/i.test(form.name ?? ""),
            )
            .reduce((summe, form) => summe + form.paragraphs.length, 0);

          if (punkte < 3) {
            return fail(
              `Die Agendafolie (Folie ${kandidat.index + 1}) hat erst ${punkte} Punkte.`,
              "Drei bis fünf Punkte geben Orientierung. Weniger sagt zu wenig, mehr kündigt nur Länge an.",
            );
          }
          if (punkte > 5) {
            return fail(
              `Die Agendafolie (Folie ${kandidat.index + 1}) listet ${punkte} Punkte auf.`,
              "Fasse zusammen: Drei bis fünf Punkte sind Orientierung, alles darüber ist eine Inhaltsübersicht.",
            );
          }

          return pass(
            `Folie ${kandidat.index + 1} ist eine Agendafolie mit ${punkte} Punkten – genau die Größenordnung, die Orientierung gibt.`,
          );
        },
      },
    },
    {
      id: "zoom",
      title: "Zoom-Folien",
      explanation: [
        "„Einfügen“ → „Zoom“ erzeugt eine Folie, auf der Miniaturen anderer Folien oder " +
          "Abschnitte liegen. Ein Klick darauf springt während der Präsentation dorthin " +
          "und – bei aktivierter Option – anschließend wieder zurück.",
        "Es gibt drei Varianten. Der Zusammenfassungszoom erzeugt automatisch eine " +
          "Übersichtsfolie aus den Abschnitten, inklusive Abschnitten, falls noch keine " +
          "existieren. Der Abschnittszoom verweist auf einen bestimmten Abschnitt, der " +
          "Folienzoom auf eine einzelne Folie.",
        "Der Vorteil gegenüber einer getippten Agenda ist die Verknüpfung: Ändert sich " +
          "der Titel einer Folie, ändert sich die Miniatur mit. Der Nachteil ist die " +
          "Abhängigkeit von der Reihenfolge – wer Folien zwischen Abschnitten verschiebt, " +
          "sollte den Zoom danach kurz kontrollieren.",
        "Für einen Vortrag, der je nach Publikum unterschiedliche Wege nimmt, ist der " +
          "Zusammenfassungszoom das passende Werkzeug: Man springt gezielt in den " +
          "Abschnitt, der gerade gefragt ist, und landet danach wieder auf der Übersicht.",
      ],
      keyPoints: [
        "Einfügen → Zoom → Zusammenfassungszoom, Abschnittszoom oder Folienzoom.",
        "Zoom-Miniaturen sind verknüpft und aktualisieren sich mit.",
        "Nach dem Umsortieren von Folien den Zoom kontrollieren.",
      ],
    },
  ],
};
